import { useEffect, useRef } from 'react';

import { User } from '@auth0/auth0-react';

import {
  EmbeddedFirstLoginMessageType,
  usePostMessageToParent,
} from '@/hooks/postMessageToParent';
import { CLAIM_EXTERNAL_COMPANY_ID } from '@/pages/EmbeddedFirstLogin/parseEmbeddedFirstLoginArgs';

type Auth0UserWithExternalCompanyId = {
  [CLAIM_EXTERNAL_COMPANY_ID]?: string;
  [key: string]: unknown;
};

type Props = {
  isEmbedded: boolean;
  isReady: boolean;
  organizationId: string | undefined;
  auth0User: User | null | undefined;
};

/**
 * Posts SignupCompleteMessage to the parent frame once the embedded app has fully
 * initialized for a returning user who has gone through an embedded signup.
 *
 * This handles the edge case where:
 * 1. User starts embedded signup and EmbeddedFirstLogin posts the message
 * 2. User closes browser before the post-signup redirect completes
 * 3. User comes back as a "returning user" — we re-post to ensure parent has the org IDs
 *
 * We only post if the user has the external company ID claim, which indicates they
 * went through an embedded signup flow (not a storefront or other embedded flow).
 */
export const usePostEmbeddedSignupComplete = ({
  isEmbedded,
  isReady,
  organizationId,
  auth0User,
}: Props) => {
  const postMessageToParent = usePostMessageToParent();
  const hasPostedRef = useRef(false);

  useEffect(() => {
    if (!isEmbedded || !isReady || !organizationId || hasPostedRef.current) {
      return;
    }

    const externalOrgId = (auth0User as Auth0UserWithExternalCompanyId)?.[
      CLAIM_EXTERNAL_COMPANY_ID
    ];

    // Only post if the user has the external company ID claim
    // This indicates they went through an embedded signup, not just accessing a product page
    if (!externalOrgId) {
      return;
    }

    hasPostedRef.current = true;

    postMessageToParent({
      type: EmbeddedFirstLoginMessageType.SIGNUP_COMPLETE,
      organization_id: organizationId,
      external_organization_id: externalOrgId,
      timestamp: new Date().toISOString(),
    });
  }, [isEmbedded, isReady, organizationId, auth0User, postMessageToParent]);
};
